import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import html2pdf from 'html2pdf.js/dist/html2pdf.min.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import '../../styles/ReportTemplate.css';
import ReportEntityDrawer from './ReportEntityDrawer';
import { patientCardPath } from '../../utils/patientCardLink';
import { loadReportRun, saveReportRun } from '../../utils/reportSession';
import { reportQueryErrorMessage } from '../../utils/reportQueryError';
import {
  REPORT_ROW_SOFT_CAP,
  buildTableModel,
  filtersFromSearch,
  filtersToSearch,
  getColumnLink,
  mappedReportFilters,
  parseDisplayConfig,
  parseFilterDefs,
  resolveLinkTarget,
  tableModelToAoa,
  collectStyleRules,
  normalizeFormat,
  normalizeStyle,
} from '../../utils/reportDisplay';

const ReportTemplate = () => {
  const { reportId: reportIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const reportId = reportIdParam || location.state?.id;

  const [reportName, setReportName] = useState(location.state?.name || '');
  const [config, setConfig] = useState(null);
  const [filters, setFilters] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [sortSpec, setSortSpec] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [drawerTarget, setDrawerTarget] = useState(null);
  const [hasRun, setHasRun] = useState(false);
  const [runError, setRunError] = useState('');

  const reportTableRef = useRef();

  const breadcrumb = location.state?.breadcrumb || [];
  const displayConfig = useMemo(() => parseDisplayConfig(config?.display), [config]);
  const filterDefs = useMemo(() => parseFilterDefs(config?.filters), [config]);

  const loadDropdownOptions = useCallback(async (id, filterConfigs) => {
    const newDropdownOptions = {};
    for (const filter of filterConfigs) {
      if (filter.type === 'dropdown' && filter.optionsQuery) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/reports/${id}/filter-options/${filter.name}`
          );
          newDropdownOptions[filter.name] = response.data;
        } catch (error) {
          console.error(`Error loading options for filter ${filter.name}:`, error);
          toast.error(reportQueryErrorMessage(error, `Error loading options for ${filter.label}`), { autoClose: 12000 });
        }
      }
    }
    setDropdownOptions(newDropdownOptions);
  }, []);

  useEffect(() => {
    if (!reportId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setDrawerTarget(null);
    setHasRun(false);
    setRunError('');
    setData([]);

    axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/reports/${reportId}`)
      .then((res) => {
        if (cancelled) return;
        const report = res.data;
        setConfig(report);
        setReportName(report.report_title || location.state?.name || '');
        const defs = parseFilterDefs(report.filters);
        const defaults = {};
        defs.forEach((filter) => {
          defaults[filter.name] = filter.default || '';
        });
        const incoming = location.state?.filters || {};
        const fromUrl = filtersFromSearch(location.search, defs, defaults);
        const nextFilters = { ...defaults, ...fromUrl, ...incoming };
        setFilters(nextFilters);

        const parsedDisplay = parseDisplayConfig(report.display);
        const initialCollapsed = {};
        if (parsedDisplay.grouping && parsedDisplay.grouping.collapsedByDefault) {
          initialCollapsed.__defaultCollapsed = true;
        }
        setCollapsedGroups(initialCollapsed);
        setSortSpec(parsedDisplay.defaultSort || []);

        const cached = loadReportRun(reportId);
        if (cached && cached.data && JSON.stringify(cached.filters || {}) === JSON.stringify(nextFilters)) {
          setData(cached.data);
          setHasRun(true);
          if (cached.sortSpec) setSortSpec(cached.sortSpec);
        }

        loadDropdownOptions(reportId, defs);
      })
      .catch((err) => {
        console.error('Error loading report configuration:', err);
        toast.error('Error loading report configuration.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reportId, loadDropdownOptions]);

  const syncFiltersToUrl = (nextFilters) => {
    const nextSearch = filtersToSearch(nextFilters).replace(/^\?/, '');
    const currentSearch = searchParams.toString();
    if (nextSearch !== currentSearch) {
      setSearchParams(nextSearch ? Object.fromEntries(new URLSearchParams(nextSearch)) : {}, { replace: true });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const runReport = () => {
    if (!reportId) return;
    setIsRunning(true);
    setRunError('');
    const normalizedFilters = Object.fromEntries(
      Object.entries(filters).map(([key, value]) => [key, value !== undefined && value !== '' ? value : null])
    );
    syncFiltersToUrl(filters);

    axios.post(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/reports/${reportId}/run`, { filters: normalizedFilters })
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        setData(rows);
        setHasRun(true);
        if (rows.length > REPORT_ROW_SOFT_CAP) {
          toast.warn(`This report returned ${rows.length.toLocaleString()} rows. Consider aggregating in SQL for faster loading.`);
        }
        saveReportRun(reportId, { filters, data: rows, name: reportName, sortSpec });
      })
      .catch((err) => {
        console.error('Error running report:', err);
        const message = reportQueryErrorMessage(err, 'Error running report.');
        setRunError(message);
        toast.error(message, { autoClose: 12000 });
      })
      .finally(() => setIsRunning(false));
  };

  const tableModel = useMemo(
    () => buildTableModel(data, displayConfig, { sort: sortSpec, collapsedGroups }),
    [data, displayConfig, sortSpec, collapsedGroups]
  );

  useEffect(() => {
    const groupCols = (displayConfig.grouping && displayConfig.grouping.columns) || [];
    if (!displayConfig.grouping || !displayConfig.grouping.collapsedByDefault || !groupCols.length) return;
    if (!data.length) return;
    if (Object.keys(collapsedGroups).some((key) => key !== '__defaultCollapsed')) return;
    const model = buildTableModel(data, displayConfig, { sort: sortSpec, collapsedGroups: {} });
    if (!model.groups.length) return;
    const next = { __defaultCollapsed: true };
    model.groups.forEach((group) => {
      next[group.key] = true;
    });
    setCollapsedGroups(next);
  }, [data, displayConfig, sortSpec, collapsedGroups]);

  const formatCellValue = (columnName, value) => {
    const columnConfig = displayConfig.columns && displayConfig.columns[columnName];
    const format = columnConfig ? normalizeFormat(columnConfig.format) : null;
    if (format) {
      if (value === null || value === undefined || value === '') return value;
      switch (format.type) {
        case 'currency': {
          const currency = format.currency || 'USD';
          const numeric = parseFloat(value);
          if (Number.isNaN(numeric)) return value;
          return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(numeric);
        }
        case 'number': {
          const decimals = format.decimals || 0;
          const numeric = parseFloat(value);
          if (Number.isNaN(numeric)) return value;
          return numeric.toFixed(decimals);
        }
        case 'percentage': {
          const numeric = parseFloat(value);
          if (Number.isNaN(numeric)) return value;
          return `${(numeric * 100).toFixed(1)}%`;
        }
        case 'date': {
          const date = new Date(value);
          return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
        }
        case 'datetime': {
          const date = new Date(value);
          return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
        }
        default:
          return value;
      }
    }
    return value;
  };

  const evaluateCondition = (value, operator, conditionValue) => {
    if (value === null || value === undefined) {
      return operator === '==' ? (conditionValue === null || conditionValue === undefined) : false;
    }
    const cleanValue = typeof value === 'string' ? value.replace(/,/g, '') : value;
    const cleanCondition = typeof conditionValue === 'string' ? conditionValue.replace(/,/g, '') : conditionValue;
    const numValue = parseFloat(cleanValue);
    const numCondition = parseFloat(cleanCondition);
    const isNumeric = !Number.isNaN(numValue) && !Number.isNaN(numCondition);

    switch (operator) {
      case '<':
        return isNumeric ? numValue < numCondition : false;
      case '>':
        return isNumeric ? numValue > numCondition : false;
      case '<=':
        return isNumeric ? numValue <= numCondition : false;
      case '>=':
        return isNumeric ? numValue >= numCondition : false;
      case '==':
        return isNumeric ? numValue === numCondition : String(value) === String(conditionValue);
      case '!=':
        return isNumeric ? numValue !== numCondition : String(value) !== String(conditionValue);
      default:
        return false;
    }
  };

  const getCellStyle = (columnName, value, row) => {
    const columnConfig = displayConfig.columns && displayConfig.columns[columnName];
    const rules = collectStyleRules(columnConfig);
    let computedStyle = {};
    rules.forEach((rule) => {
      if (rule.always) {
        computedStyle = { ...computedStyle, ...normalizeStyle(rule.style) };
        return;
      }
      if (!rule.condition) return;
      const { field, operator, value: conditionValue } = rule.condition;
      const cellValue = field ? row[field] : value;
      if (evaluateCondition(cellValue, operator, conditionValue)) {
        computedStyle = { ...computedStyle, ...normalizeStyle(rule.style) };
      }
    });
    return computedStyle;
  };

  const getRowStyle = (row) => {
    let computedStyle = {};
    Object.entries(displayConfig.columns || {}).forEach(([columnName, columnConfig]) => {
      const rules = collectStyleRules(columnConfig);
      rules.forEach((rule) => {
        if (!rule.applyToRow || !rule.condition) return;
        const { field, operator, value: conditionValue } = rule.condition;
        const cellValue = field ? row[field] : row[columnName];
        if (evaluateCondition(cellValue, operator, conditionValue)) {
          computedStyle = { ...computedStyle, ...normalizeStyle(rule.style) };
        }
      });
    });
    return computedStyle;
  };

  const handleSort = (column) => {
    setSortSpec((prev) => {
      const current = prev[0];
      if (current && current.column === column) {
        return [{ column, direction: current.direction === 'asc' ? 'desc' : 'asc' }];
      }
      return [{ column, direction: 'asc' }];
    });
  };

  const toggleGroup = (groupKey) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      __defaultCollapsed: false,
      [groupKey]: !prev[groupKey],
    }));
  };

  const openPatientDrawer = (value) => {
    const path = patientCardPath(value);
    if (!path) {
      toast.error('Could not open this patient from the selected value.');
      return;
    }
    const params = new URLSearchParams(path.split('?')[1] || '');
    setDrawerTarget({
      type: 'patient',
      patientId: params.get('patientId'),
      patientNumber: params.get('patientNumber'),
      fullPagePath: path,
    });
  };

  const handleColumnClick = (columnName, value, row) => {
    const linkTo = getColumnLink(displayConfig, columnName);
    const target = resolveLinkTarget(linkTo, row);
    if (!target) return;

    if (target.type === 'patient') {
      openPatientDrawer(target.value);
      return;
    }
    if (target.type === 'admission') {
      if (target.value == null || target.value === '') {
        toast.error('This row has no admission id.');
        return;
      }
      setDrawerTarget({
        type: 'admission',
        admissionId: target.value,
      });
      return;
    }
    if (target.type === 'report' && target.reportId) {
      const mapped = mappedReportFilters(target.parameterMapping, row, {});
      navigate(`/reports-dashboard/${target.reportId}${filtersToSearch(mapped)}`, {
        state: {
          filters: mapped,
          breadcrumb: [...breadcrumb, { id: reportId, name: reportName, search: location.search }],
        },
      });
    }
  };

  const downloadExcel = () => {
    if (data.length === 0) {
      toast.error('No data available to download.');
      return;
    }
    const exportModel = buildTableModel(data, displayConfig, { sort: sortSpec, collapsedGroups: {} });
    const aoa = tableModelToAoa(exportModel, (column, value, row, item) => {
      if (item.type === 'subtotal' || item.type === 'grandTotal') return value;
      return formatCellValue(column, value);
    });
    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report Data');
    XLSX.writeFile(workbook, `${reportName || 'report'}.xlsx`);
  };

  const downloadPdf = () => {
    if (data.length === 0) {
      toast.error('No data available to download.');
      return;
    }
    if (!reportTableRef.current) return;
    reportTableRef.current.classList.add('pdf-mode');
    const columnCount = tableModel.columns.length;
    const orientation = columnCount > 6 ? 'landscape' : 'portrait';
    html2pdf()
      .from(reportTableRef.current)
      .set({
        margin: 0.5,
        filename: `${reportName || 'report'}.pdf`,
        jsPDF: { unit: 'in', format: 'a4', orientation },
        pagebreak: { mode: 'avoid-all' },
        html2canvas: { scale: 2 },
      })
      .save()
      .then(() => {
        reportTableRef.current.classList.remove('pdf-mode');
      })
      .catch((error) => {
        console.error('PDF generation error:', error);
        toast.error('Error generating PDF.');
        reportTableRef.current.classList.remove('pdf-mode');
      });
  };

  if (loading) {
    return <p>Loading report configuration...</p>;
  }

  if (!reportId || !config) {
    return (
      <div className="report-template">
        <p>Report not found. <Link to="/reports-dashboard">Back to reports</Link></p>
      </div>
    );
  }

  return (
    <div className="report-template">
      <ToastContainer />
      {breadcrumb.length > 0 && (
        <nav className="report-breadcrumb">
          {breadcrumb.map((crumb, index) => (
            <span key={`${crumb.id}-${index}`}>
              <Link to={`/reports-dashboard/${crumb.id}${crumb.search || ''}`}>{crumb.name || `Report ${crumb.id}`}</Link>
              <span className="report-breadcrumb-sep"> / </span>
            </span>
          ))}
          <span>{reportName}</span>
        </nav>
      )}
      <h2>{reportName}</h2>

      {filterDefs.length > 0 && (
        <div className="filter-form">
          {filterDefs.map((filter) => (
            <div key={filter.name} className="filter-input">
              <label htmlFor={filter.name}>{filter.label}:</label>
              {filter.type === 'dropdown' ? (
                <select
                  id={filter.name}
                  name={filter.name}
                  value={filters[filter.name] || ''}
                  onChange={handleInputChange}
                >
                  <option value="">Select an option</option>
                  {(dropdownOptions[filter.name] || filter.options || []).map((option) => (
                    <option key={option.id || option.value} value={option.id || option.value}>
                      {option.label || option.value || option.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={filter.name}
                  type={filter.type || 'text'}
                  name={filter.name}
                  value={filters[filter.name] || ''}
                  onChange={handleInputChange}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <button type="button" onClick={runReport} disabled={isRunning}>
        {isRunning ? 'Running...' : hasRun ? 'Run again' : 'Run Report'}
      </button>

      {runError && (
        <div className="report-run-error" role="alert">
          <strong>Could not run this report</strong>
          <pre className="report-sql-error-detail">{runError}</pre>
        </div>
      )}

      {hasRun && data.length === 0 && !isRunning && !runError && (
        <p className="report-empty">No rows matched these filters.</p>
      )}

      {data.length > 0 && (
        <>
          <div className="report-results" ref={reportTableRef}>
            <div className="report-results-header">
              <h3>{reportName}</h3>
              <span className="report-row-count">{data.length.toLocaleString()} rows</span>
            </div>
            <div className="report-table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    {tableModel.columns.map((col) => {
                      const active = sortSpec[0] && sortSpec[0].column === col;
                      return (
                        <th key={col} onClick={() => handleSort(col)} className="sortable-header">
                          {col}
                          {active ? (sortSpec[0].direction === 'asc' ? ' ▲' : ' ▼') : ''}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {tableModel.items.map((item) => {
                    if (item.type === 'group') {
                      return (
                        <tr key={item.key} className="report-group-row">
                          <td colSpan={tableModel.columns.length}>
                            <button type="button" className="report-group-toggle" onClick={() => toggleGroup(item.groupKey)}>
                              <FontAwesomeIcon icon={item.collapsed ? faChevronRight : faChevronDown} />
                              {item.label}
                              <span className="report-group-count">({item.count})</span>
                            </button>
                          </td>
                        </tr>
                      );
                    }
                    const rowClass = item.type === 'grandTotal'
                      ? 'report-grand-total-row'
                      : item.type === 'subtotal'
                        ? 'report-subtotal-row'
                        : '';
                    const isSummary = item.type === 'subtotal' || item.type === 'grandTotal';
                    const rowStyle = isSummary ? {} : getRowStyle(item.row);
                    return (
                      <tr key={item.key} className={rowClass}>
                        {tableModel.columns.map((col) => {
                          const value = item.row[col];
                          const formattedValue = (isSummary && (value === '' || value === null || value === undefined))
                            ? ''
                            : formatCellValue(col, value);
                          const isClickable = !isSummary && Boolean(getColumnLink(displayConfig, col));
                          const cellStyle = isSummary ? {} : getCellStyle(col, value, item.row);
                          const combinedStyle = {
                            ...normalizeStyle(rowStyle),
                            ...normalizeStyle(cellStyle),
                            ...(isClickable ? { cursor: 'pointer', color: '#007bff', textDecoration: 'underline' } : {}),
                          };
                          return (
                            <td
                              key={col}
                              className={isClickable ? 'clickable-cell' : ''}
                              onClick={isClickable ? () => handleColumnClick(col, value, item.row) : undefined}
                              style={combinedStyle}
                            >
                              {formattedValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="download-buttons">
            <button type="button" onClick={downloadExcel} className="download-button">
              Download Excel
            </button>
            <button type="button" onClick={downloadPdf} className="download-button">
              Download PDF
            </button>
          </div>
        </>
      )}

      <ReportEntityDrawer target={drawerTarget} onClose={() => setDrawerTarget(null)} />
    </div>
  );
};

export default ReportTemplate;
